import { useMemo, useState } from "react";
import { hotComics } from "@/mocks/comics";

interface HotComicsProps {
  search: string;
  genre: string;
  setGenre: (v: string) => void;
}

type Comic = (typeof hotComics)[number];

export default function HotComics({ search, genre, setGenre }: HotComicsProps) {
  const [active, setActive] = useState<Comic | null>(null);
  const [toast, setToast] = useState("");

  const genres = useMemo(
    () => Array.from(new Set(hotComics.map((c) => c.genre))),
    []
  );

  const list = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return hotComics.filter((c) => {
      const matchGenre = !genre || c.genre === genre;
      const matchKw =
        !kw ||
        c.title.toLowerCase().includes(kw) ||
        c.genre.toLowerCase().includes(kw) ||
        c.author.toLowerCase().includes(kw);
      return matchGenre && matchKw;
    });
  }, [genre, search]);

  const startCache = () => {
    setToast("已加入缓存队列，正在为你离线下载，稍后即可畅读！");
    window.setTimeout(() => setToast(""), 2600);
  };

  return (
    <section id="comics" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary-600">
            热门漫画
          </p>
          <h2 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            大家都在离线追的漫画
          </h2>
          <p className="mt-4 text-base text-foreground-600">
            精选人气作品，点击卡片即可查看详情并一键缓存离线收藏。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setGenre("")}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              genre === ""
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
            }`}
          >
            全部
          </button>
          {genres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGenre(genre === g ? "" : g)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                genre === g
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-14 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
            <i className="ri-search-line text-4xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-4 text-base font-medium text-foreground-700">
              没有找到相关的漫画作品
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词或类型再试试吧
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActive(c)}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 text-left transition-all hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden">
                  <img
                    src={c.cover}
                    alt={`${c.title}漫画封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 rounded-full bg-primary-500/90 px-2.5 py-0.5 text-xs font-semibold text-background-50">
                    {c.status}
                  </span>
                  <span className="absolute right-2 top-2 flex h-7 items-center gap-0.5 rounded-full bg-background-950/60 px-2 text-xs font-bold text-accent-300 backdrop-blur-sm">
                    <i className="ri-star-fill text-sm" aria-hidden="true"></i>
                    {c.rating}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-1 p-3">
                  <h3 className="truncate text-sm font-bold text-foreground-900 md:text-base">
                    {c.title}
                  </h3>
                  <p className="text-xs text-foreground-500">
                    {c.author} · {c.chapters}话
                  </p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-900">
                      {c.genre}
                    </span>
                    <span className="text-xs text-foreground-500">{c.tag}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background-950/50 p-4"
          onClick={() => setActive(null)}
        >
          <div
            className="grid max-h-[90vh] w-full max-w-3xl grid-cols-1 overflow-y-auto rounded-lg bg-background-50 sm:grid-cols-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[3/4] w-full overflow-hidden sm:h-full">
              <img
                src={active.cover}
                alt={`${active.title}漫画封面大图`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-2xl font-black text-foreground-950">
                    {active.title}
                  </h3>
                  <p className="mt-1 text-sm text-foreground-500">
                    {active.author} · {active.genre} · {active.tag}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600 transition-colors hover:bg-background-200"
                  aria-label="关闭详情"
                >
                  <i className="ri-close-line text-xl" aria-hidden="true"></i>
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-md bg-secondary-100 px-3 py-1 text-sm text-secondary-900">
                  共 {active.chapters} 话
                </span>
                <span className="rounded-md bg-accent-100 px-3 py-1 text-sm text-accent-900">
                  评分 {active.rating}
                </span>
                <span className="rounded-md bg-primary-100 px-3 py-1 text-sm text-primary-700">
                  {active.status}
                </span>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-foreground-600">
                《{active.title}》是{active.genre}题材的人气作品，由{active.author}
                创作。故事节奏明快、人设鲜明，目前更新至第{active.chapters}
                话。通过洒洒漫画网可一键缓存整部作品，全集打包离线收藏，通勤路上、无网络环境都能畅快追读。
              </p>

              <div className="mt-auto flex flex-col gap-2 pt-6 sm:flex-row">
                <button
                  type="button"
                  onClick={startCache}
                  className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-download-2-line text-base" aria-hidden="true"></i>
                  缓存全集离线看
                </button>
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-bookmark-line text-base" aria-hidden="true"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 shadow-soft">
          {toast}
        </div>
      )}
    </section>
  );
}